import React from 'react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { 
  Drawer, DrawerContent, DrawerTitle, DrawerDescription 
} from '../ui/drawer';
import { CheckCircle2, Clock, Package, FileText, Zap } from 'lucide-react';

export default function TarefasDrawer({
  open,
  onOpenChange,
  tarefasAtribuidas = [],
  tarefasHistoricoDia = [],
  tarefasTab,
  onTabChange,
  onAbrirTarefa,
}) {
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[85vh]">
        <div className="px-4 py-3 border-b border-stone-100 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
            <FileText className="h-5 w-5 text-purple-600" />
          </div>
          <div>
            <DrawerTitle className="text-lg font-semibold">Tarefas Pontuais</DrawerTitle>
            <p className="text-sm text-stone-500">
              {tarefasAtribuidas.length} disponíve{tarefasAtribuidas.length !== 1 ? 'is' : 'l'} · {tarefasHistoricoDia.length} concluída{tarefasHistoricoDia.length !== 1 ? 's' : ''} hoje
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-4 pt-3 flex gap-2">
          <button
            onClick={() => onTabChange('disponiveis')}
            className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-medium transition-all ${
              tarefasTab === 'disponiveis'
                ? 'bg-purple-600 text-white'
                : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
            }`}
          >
            Disponíveis ({tarefasAtribuidas.length})
          </button>
          <button
            onClick={() => onTabChange('historico')}
            className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-medium transition-all ${
              tarefasTab === 'historico'
                ? 'bg-green-600 text-white'
                : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
            }`}
          >
            Histórico Dia ({tarefasHistoricoDia.length})
          </button>
        </div>

        <div className="p-4 overflow-y-auto max-h-[55vh]">
          {/* Tab Disponíveis */}
          {tarefasTab === 'disponiveis' && (
            <>
              {tarefasAtribuidas.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-stone-100 flex items-center justify-center">
                    <FileText className="h-8 w-8 text-stone-400" />
                  </div>
                  <p className="text-stone-500 font-medium">Sem tarefas disponíveis</p>
                  <p className="text-sm text-stone-400 mt-1">As tarefas atribuídas aparecerão aqui</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {tarefasAtribuidas.map((tarefa) => (
                    <Card
                      key={tarefa.id}
                      className={`p-4 cursor-pointer hover:shadow-md transition-shadow ${
                        tarefa.urgente ? 'border-l-4 border-l-red-500' : ''
                      }`}
                      onClick={() => onAbrirTarefa(tarefa)}
                      data-testid={`tarefa-card-${tarefa.id}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-medium text-stone-800">{tarefa.nome}</h4>
                        {tarefa.urgente && (
                          <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full font-medium flex items-center gap-1">
                            <Zap className="h-3 w-3" />
                            Urgente
                          </span>
                        )}
                      </div>

                      {(tarefa.marca || tarefa.modelo) && (
                        <p className="text-sm text-stone-500 mb-2">
                          {tarefa.marca && <span className="font-medium">{tarefa.marca}</span>}
                          {tarefa.marca && tarefa.modelo && ' · '}
                          {tarefa.modelo && <span><Package className="h-3 w-3 inline mr-1" />{tarefa.modelo}</span>}
                        </p>
                      )}

                      <div className="flex flex-wrap gap-1 mb-2">
                        {(tarefa.operacoes || []).map((op, idx) => (
                          <span key={idx} className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded font-mono">
                            {op.operacao_codigo}
                          </span>
                        ))}
                      </div>

                      {tarefa.observacoes && (
                        <p className="text-xs text-stone-400 italic mt-2">{tarefa.observacoes}</p>
                      )}

                      <div className="mt-3 pt-2 border-t border-stone-100 flex justify-end">
                        <Button size="sm" className="rounded-full bg-purple-500 hover:bg-purple-600">
                          <CheckCircle2 className="h-4 w-4 mr-2" />
                          Concluir
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Tab Histórico do Dia */}
          {tarefasTab === 'historico' && (
            <>
              {tarefasHistoricoDia.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-stone-100 flex items-center justify-center">
                    <Clock className="h-8 w-8 text-stone-400" />
                  </div>
                  <p className="text-stone-500 font-medium">Sem tarefas concluídas hoje</p>
                  <p className="text-sm text-stone-400 mt-1">As tarefas terminadas aparecerão aqui</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {tarefasHistoricoDia.map((tarefa) => (
                    <Card
                      key={tarefa.id}
                      className="p-4 bg-green-50/50 border-green-200"
                      data-testid={`tarefa-historico-${tarefa.id}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-medium text-stone-800">{tarefa.nome}</h4>
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium flex items-center gap-1">
                          <CheckCircle2 className="h-3 w-3" />
                          Concluída
                        </span>
                      </div>

                      {(tarefa.marca || tarefa.modelo) && (
                        <p className="text-sm text-stone-500 mb-2">
                          {tarefa.marca && <span className="font-medium">{tarefa.marca}</span>}
                          {tarefa.marca && tarefa.modelo && ' · '}
                          {tarefa.modelo && <span><Package className="h-3 w-3 inline mr-1" />{tarefa.modelo}</span>}
                        </p>
                      )}

                      <div className="flex flex-wrap gap-1 mb-2">
                        {(tarefa.operacoes || []).map((op, idx) => (
                          <span key={idx} className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded font-mono">
                            {op.operacao_codigo}
                          </span>
                        ))}
                      </div>

                      <div className="mt-2 pt-2 border-t border-green-200 flex items-center justify-between">
                        <div className="flex items-center gap-3 text-sm">
                          <span className="text-green-700 font-semibold">
                            {tarefa.quantidade_final} peças
                          </span>
                          {tarefa.concluido_em && (
                            <span className="text-stone-400 flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {new Date(tarefa.concluido_em).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="p-4 border-t border-stone-100">
          <Button
            onClick={() => onOpenChange(false)}
            className="w-full h-12 rounded-xl"
            variant="outline"
          >
            Fechar
          </Button>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
